/**
 * Page-level Help FAQs for working clinic routes.
 * Merged into helpContent.js categories, items, and support shortcuts.
 */

export const SCHEDULING_GROUP_ID = 'scheduling';
export const ENGAGEMENT_GROUP_ID = 'engagement';
export const COMMUNICATION_GROUP_ID = 'communication';
export const SETTINGS_GROUP_ID = 'settings';

export const PAGE_FAQ_CATEGORY_GROUPS = [
    {
        id: SCHEDULING_GROUP_ID,
        title: 'Scheduling',
        icon: 'bi-calendar-check',
        theme: 'purple',
    },
    {
        id: ENGAGEMENT_GROUP_ID,
        title: 'Engagement',
        icon: 'bi-heart',
        theme: 'warning',
    },
    {
        id: COMMUNICATION_GROUP_ID,
        title: 'Communication',
        icon: 'bi-chat-left-text',
        theme: 'success',
    },
    {
        id: SETTINGS_GROUP_ID,
        title: 'Settings',
        icon: 'bi-gear',
        theme: 'slate',
    },
];

export const PAGE_FAQ_CATEGORIES = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        icon: 'bi-speedometer2',
        theme: 'primary',
    },
    {
        id: 'appointments',
        parentId: SCHEDULING_GROUP_ID,
        title: 'Appointments',
        icon: 'bi-calendar-event',
        theme: 'purple',
    },
    {
        id: 'web-bookings',
        parentId: SCHEDULING_GROUP_ID,
        title: 'Web Bookings',
        icon: 'bi-calendar-check',
        theme: 'cyan',
    },
    {
        id: 'optical-dispensary',
        parentId: SCHEDULING_GROUP_ID,
        title: 'Optical Dispensary',
        icon: 'bi-eyeglasses',
        theme: 'violet',
    },
    {
        id: 'cancellation-followups',
        parentId: ENGAGEMENT_GROUP_ID,
        title: 'Cancellation Followups',
        icon: 'bi-calendar-x',
        theme: 'danger',
    },
    {
        id: 'patient-engagement',
        parentId: ENGAGEMENT_GROUP_ID,
        title: 'Patient Engagement',
        icon: 'bi-person-hearts',
        theme: 'pink',
    },
    {
        id: 'reputation',
        parentId: ENGAGEMENT_GROUP_ID,
        title: 'Reputation',
        icon: 'bi-star',
        theme: 'warning',
    },
    {
        id: 'recalls',
        parentId: ENGAGEMENT_GROUP_ID,
        title: 'Recalls',
        icon: 'bi-person-check',
        theme: 'orange',
    },
    {
        id: 'invalid-details',
        parentId: ENGAGEMENT_GROUP_ID,
        title: 'Invalid Details',
        icon: 'bi-exclamation-octagon',
        theme: 'danger',
    },
    {
        id: 'messages',
        parentId: COMMUNICATION_GROUP_ID,
        title: 'Messages',
        icon: 'bi-chat-left-text',
        theme: 'success',
    },
    {
        id: 'surveys',
        parentId: COMMUNICATION_GROUP_ID,
        title: 'Surveys',
        icon: 'bi-clipboard-check',
        theme: 'teal',
    },
    {
        id: 'articles',
        parentId: COMMUNICATION_GROUP_ID,
        title: 'Articles',
        icon: 'bi-file-text',
        theme: 'orange',
    },
    {
        id: 'newsletter',
        parentId: COMMUNICATION_GROUP_ID,
        title: 'Newsletter',
        icon: 'bi-envelope-paper',
        theme: 'pink',
    },
    {
        id: 'blast',
        parentId: COMMUNICATION_GROUP_ID,
        title: 'Blast',
        icon: 'bi-megaphone',
        theme: 'danger',
    },
    {
        id: 'settings-clinic',
        parentId: SETTINGS_GROUP_ID,
        title: 'Clinic Settings',
        icon: 'bi-building',
        theme: 'slate',
    },
    {
        id: 'settings-reminders',
        parentId: SETTINGS_GROUP_ID,
        title: 'Reminders',
        icon: 'bi-bell',
        theme: 'warning',
    },
    {
        id: 'settings-templates',
        parentId: SETTINGS_GROUP_ID,
        title: 'Templates',
        icon: 'bi-file-earmark-text',
        theme: 'cyan',
    },
    {
        id: 'settings-users',
        parentId: SETTINGS_GROUP_ID,
        title: 'Users',
        icon: 'bi-people',
        theme: 'indigo',
    },
    {
        id: 'settings-features',
        parentId: SETTINGS_GROUP_ID,
        title: 'Features',
        icon: 'bi-toggles',
        theme: 'violet',
    },
    {
        id: 'invoices',
        title: 'Invoices',
        icon: 'bi-receipt',
        theme: 'slate',
    },
    {
        id: 'eye-draw',
        title: 'Eye Draw',
        icon: 'bi-easel',
        theme: 'indigo',
    },
    {
        id: 'whats-new',
        title: "What's New",
        icon: 'bi-stars',
        theme: 'primary',
    },
];

export const PAGE_FAQ_ITEMS = [
    // ── Dashboard ──────────────────────────────────────────────
    {
        id: 'dashboard-overview',
        categoryId: 'dashboard',
        question: 'What is the Dashboard?',
        answer:
            'Dashboard is your clinic overview. Use the section tabs to switch between Patients, ROI, Appointments Ratio (when Appointments is enabled), Email Stats, SMS Stats, and Campaigns. When Appointments is enabled, a side panel shows today\'s schedule with ECP and date filters.',
    },
    {
        id: 'dashboard-access',
        categoryId: 'dashboard',
        question: 'How do I open the Dashboard?',
        answer:
            'Click Dashboard in the left sidebar. It is available to all clinic users and does not require a separate feature toggle.',
    },
    {
        id: 'dashboard-email-stats',
        categoryId: 'dashboard',
        question: 'How do Email Stats work?',
        answer:
            'Open the Email Stats tab to see outbound email volume for your selected date range. Use the group control to view All emails, Patient Emails only, or System Emails only. The bar chart updates to match the selected group.',
    },
    {
        id: 'dashboard-sms-stats',
        categoryId: 'dashboard',
        question: 'How do SMS Stats work?',
        answer:
            'Open the SMS Stats tab to review outbound SMS volume. The chart defaults to All message types. Use the type filter to narrow the bar chart to a specific SMS category when you need a closer look.',
    },
    {
        id: 'dashboard-refresh',
        categoryId: 'dashboard',
        question: 'How do I refresh Dashboard data?',
        answer:
            'Click Refresh in the page header to reload appointment and stats panels. Date and ECP filters on the appointments side panel stay applied after refresh.',
    },
    {
        id: 'dashboard-patients-tab',
        categoryId: 'dashboard',
        question: 'What does the Patients tab show?',
        answer:
            'The Patients tab shows status, age group, and communication preference charts, plus patient information summaries for your clinic. Use it for a quick snapshot of your active patient population.',
    },

    // ── Appointments ───────────────────────────────────────────
    {
        id: 'appointments-overview',
        categoryId: 'appointments',
        question: 'What is Appointments?',
        answer:
            'Appointments shows your clinic schedule for a selected day. Use the calendar sidebar to pick a date, then review appointments in list or calendar view. You can filter by ECP, refresh the schedule, and open patient profiles from appointment rows.',
    },
    {
        id: 'appointments-access',
        categoryId: 'appointments',
        question: 'How do I open Appointments?',
        answer:
            'Open Appointments from the left sidebar when the Appointments feature is enabled for your clinic. If you do not see it, ask an admin to enable it under Settings → Features.',
    },
    {
        id: 'appointments-date',
        categoryId: 'appointments',
        question: 'How do I change the schedule date?',
        answer:
            'Use the calendar in the left panel, or click Previous, Today, or Next under Schedule Date. The main list updates to appointments for that day.',
    },
    {
        id: 'appointments-views',
        categoryId: 'appointments',
        question: 'What views are available?',
        answer:
            'Switch between list and calendar-style views using the view controls on the page. Your last selected view is remembered for the next visit when browser storage is available.',
    },
    {
        id: 'appointments-status',
        categoryId: 'appointments',
        question: 'What do appointment status labels mean?',
        answer:
            'Status pills show confirmation state for each appointment (for example confirmed, cancelled, or pending). Use them to scan the day quickly; cancelled appointments may also appear in Cancellation Followups when follow-up outreach is needed.',
    },

    // ── Web Bookings ───────────────────────────────────────────
    {
        id: 'web-bookings-overview',
        categoryId: 'web-bookings',
        question: 'What is Web Bookings?',
        answer:
            'Web Bookings lets you review and manage appointment requests submitted through your clinic website. New requests appear in the New tab so staff can confirm, update, or close them.',
    },
    {
        id: 'web-bookings-access',
        categoryId: 'web-bookings',
        question: 'How do I open Web Bookings?',
        answer:
            'Open Web Bookings from the sidebar when the Booking feature is enabled. Configure doctors, appointment types, visit reasons, and online booking options under Settings → Clinic → Web Booking.',
    },
    {
        id: 'web-bookings-tabs',
        categoryId: 'web-bookings',
        question: 'What do the Web Bookings tabs mean?',
        answer:
            'Tabs separate requests by processing stage (for example New versus handled requests). The page description shows how many new requests are waiting. Switch tabs to review each queue without losing counts.',
    },
    {
        id: 'web-bookings-refresh',
        categoryId: 'web-bookings',
        question: 'How do I refresh booking requests?',
        answer:
            'Click Refresh in the page header to reload tab counts and the active list. Use this after confirming requests or when patients may have submitted new bookings.',
    },

    // ── Optical Dispensary ─────────────────────────────────────
    {
        id: 'optical-overview',
        categoryId: 'optical-dispensary',
        question: 'What is Optical Dispensary?',
        answer:
            'Optical Dispensary covers pickup workflows and contact lens reorder requests. Use it to track ready pickups, search orders, and manage related optical tasks for your clinic.',
    },
    {
        id: 'optical-access',
        categoryId: 'optical-dispensary',
        question: 'How do I open Optical Dispensary?',
        answer:
            'Open Optical Dispensary from the sidebar when the feature is enabled. The default landing path is Pickups; use in-page navigation for contact lens reorder requests and related tools.',
    },
    {
        id: 'optical-pickups',
        categoryId: 'optical-dispensary',
        question: 'How do pickups work?',
        answer:
            'On Pickups, search and filter ready orders, then update status as patients collect their glasses or lenses. Use the toolbar search forms to narrow by patient or order details.',
    },
    {
        id: 'optical-cl-orders',
        categoryId: 'optical-dispensary',
        question: 'Where do contact lens reorder requests appear?',
        answer:
            'Contact lens reorder requests are managed inside Optical Dispensary alongside pickups. Search the CL order list, review request details, and process them according to your clinic workflow. Brand lists can be maintained under Clinic Settings when needed.',
    },

    // ── Cancellation Followups ─────────────────────────────────
    {
        id: 'cancellation-overview',
        categoryId: 'cancellation-followups',
        question: 'What are Cancellation Followups?',
        answer:
            'Cancellation Followups lists cancelled appointments that still need outreach. Select rows, choose Email and/or SMS channels per patient, then send follow-up messages so patients can rebook.',
    },
    {
        id: 'cancellation-access',
        categoryId: 'cancellation-followups',
        question: 'How do I open Cancellation Followups?',
        answer:
            'Open Cancellation Followups from the sidebar Engagement section when it is available for your clinic. A badge count may show how many follow-ups are waiting.',
    },
    {
        id: 'cancellation-send',
        categoryId: 'cancellation-followups',
        question: 'How do I send cancellation follow-ups?',
        answer:
            'Select one or more sendable rows, confirm the Email/SMS channel checkboxes for each patient, then use Send. Only patients with valid contact details and preferences for the chosen channel can be included.',
    },
    {
        id: 'cancellation-status',
        categoryId: 'cancellation-followups',
        question: 'What do the status labels mean?',
        answer:
            'Status pills match Activity-style labels and show the appointment or outreach state for each row. Use them to scan which cancellations still need action versus those already handled.',
    },
    {
        id: 'cancellation-print',
        categoryId: 'cancellation-followups',
        question: 'Can I print the follow-up list?',
        answer:
            'Yes. Use the print action on the page to generate a printable list of cancellation follow-ups with the current channel selections and patient details.',
    },

    // ── Patient Engagement ─────────────────────────────────────
    {
        id: 'patient-engagement-overview',
        categoryId: 'patient-engagement',
        question: 'What is Patient Engagement?',
        answer:
            'Patient Engagement (Thank You & Surveys follow-ups) helps you send post-visit thank-you messages and related outreach. Select patients, choose Email and/or SMS, then send from the follow-up queue.',
    },
    {
        id: 'patient-engagement-access',
        categoryId: 'patient-engagement',
        question: 'How do I open Patient Engagement?',
        answer:
            'Open Patient Engagement from the sidebar. The page uses tabs for thank-you follow-ups and related engagement tools when enabled for your clinic.',
    },
    {
        id: 'patient-engagement-send',
        categoryId: 'patient-engagement',
        question: 'How do I send thank-you follow-ups?',
        answer:
            'Review the follow-up list, select sendable rows, set Email/SMS channel options, then click Send. Messages use your clinic thank-you templates and respect patient communication preferences.',
    },
    {
        id: 'patient-engagement-profile',
        categoryId: 'patient-engagement',
        question: 'Can I send a thank-you from a patient profile?',
        answer:
            'Yes. On a patient profile, use quick actions to send a thank-you when the patient has valid contact details and preferences. Bulk follow-ups still live on the Patient Engagement page.',
    },
    {
        id: 'patient-engagement-print',
        categoryId: 'patient-engagement',
        question: 'Can I print the thank-you follow-up list?',
        answer:
            'Yes. Use the print action to produce a printable list of thank-you follow-ups with selected channels and patient details for staff reference.',
    },

    // ── Reputation ─────────────────────────────────────────────
    {
        id: 'reputation-overview',
        categoryId: 'reputation',
        question: 'What is Reputation?',
        answer:
            'Reputation helps you monitor review links, patient comments, and feedback after visits. Tabs cover Overview, Reputation Links, Patient Comments, and Patient Feedback.',
    },
    {
        id: 'reputation-access',
        categoryId: 'reputation',
        question: 'How do I open Reputation?',
        answer:
            'Open Reputation from the sidebar when the Reputation feature is enabled. Configure review destinations (Google, Facebook, Yelp, and others) under Reputation Links.',
    },
    {
        id: 'reputation-comments',
        categoryId: 'reputation',
        question: 'What are Patient Comments?',
        answer:
            'Patient Comments lists comments submitted through reputation/feedback flows. Each row shows the full comment ID, status (Pending, Sent, or Error), and a link to the patient profile when available. The display name may differ from the PMS legal name.',
    },
    {
        id: 'reputation-comment-status',
        categoryId: 'reputation',
        question: 'What do comment statuses mean?',
        answer:
            'Pending means the clinic notification has not finished processing. Sent means the clinic was notified and an Activity entry may be recorded. Error means delivery of the clinic notification failed—check Activity or contact support if it persists.',
    },
    {
        id: 'reputation-feedback',
        categoryId: 'reputation',
        question: 'What is Patient Feedback?',
        answer:
            'Patient Feedback shows positive, neutral, or negative responses collected from reputation enhancement links. Use it to spot unhappy visits early and follow up from the patient profile when needed.',
    },
    {
        id: 'reputation-links',
        categoryId: 'reputation',
        question: 'How do Reputation Links work?',
        answer:
            'Reputation Links store the public review destinations patients are directed to after a positive experience. Keep Google, Facebook, website, and other links current so thank-you and feedback emails route patients correctly.',
    },

    // ── Recalls ────────────────────────────────────────────────
    {
        id: 'recalls-overview',
        categoryId: 'recalls',
        question: 'What are Recalls?',
        answer:
            'Recalls has two areas: Requests (upload PMS recall files and track processing) and Pool (browse matched patients and update recall stages). Together they help EyeconX reach patients due for follow-up exams or visits.',
    },
    {
        id: 'recalls-access',
        categoryId: 'recalls',
        question: 'How do I open Recalls?',
        answer:
            'Open Recalls from the sidebar when the Recall feature is enabled. Use the Recalls submenu for Requests or Pool. Related recall hours and messaging preferences are under Clinic Settings → Recall.',
    },
    {
        id: 'recalls-upload',
        categoryId: 'recalls',
        question: 'How do I upload a recall file?',
        answer:
            'Go to Recalls → Requests, upload the file exported from your PMS, then monitor processing status on that page. If a file fails, verify the export format and try again, or check PMS Update History for related sync issues.',
    },
    {
        id: 'recalls-pool-overview',
        categoryId: 'recalls',
        question: 'What is the Recall Pool?',
        answer:
            'Recall Pool is the clinic list of patients matched from processed recall requests. Use it to scan large volumes of patients, open full entry details, and update First–Fourth recall stages. Removing pool entries is handled by EyeconX Admin.',
    },
    {
        id: 'recalls-pool-access',
        categoryId: 'recalls',
        question: 'How do I open Recall Pool?',
        answer:
            'Open Recalls → Pool from the sidebar submenu. The page uses an Activity-style list with filters and pagination so you can work through thousands of pool entries without a heavy card layout.',
    },
    {
        id: 'recalls-pool-search',
        categoryId: 'recalls',
        question: 'How do I search and filter the Recall Pool?',
        answer:
            'Choose a search field (last name, first name, email, or cell number), enter a term, then Search. Use Medium (Email, SMS, Call) and Status (Pending, In process, Sent, Failed, Completed) filters, and Sort by created date, modified date, name, or status. Clear removes active filters.',
    },
    {
        id: 'recalls-pool-details',
        categoryId: 'recalls',
        question: 'What can I see in a pool entry?',
        answer:
            'Click a row to open the details modal. It shows patient name, preferred name, status, medium, email, cell and home phones (formatted), recall number, clinic, request ID, and created/updated times. Patient and contact fields are read-only.',
    },
    {
        id: 'recalls-pool-stages',
        categoryId: 'recalls',
        question: 'How do I update recall stages?',
        answer:
            'In the pool details modal, only Recall stages can be edited. Toggle Sent for First, Second, Third, and Fourth recall and set or clear each sent date, then Save stages. Patient details, medium, and overall status cannot be changed from the clinic pool view.',
    },
    {
        id: 'recalls-pool-delete',
        categoryId: 'recalls',
        question: 'Can I delete a pool entry from the clinic portal?',
        answer:
            'No. Clinic users can review pool details and update recall stages only. Removing a pool entry is an EyeconX Admin action—contact support if an entry should be deleted.',
    },
    {
        id: 'recalls-settings',
        categoryId: 'recalls',
        question: 'Where do I configure recall messaging hours?',
        answer:
            'Go to Settings → Clinic and open the Recall section to set recall hours and related text used when contacting patients about due visits.',
    },

    // ── Invalid Details ────────────────────────────────────────
    {
        id: 'invalid-details-overview',
        categoryId: 'invalid-details',
        question: 'What is Invalid Details?',
        answer:
            'Invalid Details is a clinic-wide list of bounced or invalid patient emails and phone numbers. Use it to find records that need correction so reminders and campaigns can deliver successfully.',
    },
    {
        id: 'invalid-details-access',
        categoryId: 'invalid-details',
        question: 'How do I open Invalid Details?',
        answer:
            'Open Invalid Details from the sidebar when Invalid Details Summary is enabled. You can also review invalid contact logs on an individual patient profile under Invalid Info.',
    },
    {
        id: 'invalid-details-fix',
        categoryId: 'invalid-details',
        question: 'How do I fix an invalid email or phone?',
        answer:
            'Open the patient from the Invalid Details row (or search them in Patient Files), update the contact fields on Patient Info, and save. After the next successful delivery or sync, the invalid log should clear or stop growing for that address.',
    },
    {
        id: 'invalid-details-vs-patient',
        categoryId: 'invalid-details',
        question: 'How is this different from Invalid Info on a patient profile?',
        answer:
            'Invalid Details shows every invalid contact event across the clinic. Invalid Info on a patient profile shows only that patient\'s logs. Use the clinic page for cleanup sweeps; use the profile section when working one patient at a time.',
    },

    // ── Messages ───────────────────────────────────────────────
    {
        id: 'messages-overview',
        categoryId: 'messages',
        question: 'What is Messages?',
        answer:
            'Messages is two-way SMS with patients. Browse conversations, open a thread, and reply in real time when Two-Way Text is enabled for your clinic.',
    },
    {
        id: 'messages-access',
        categoryId: 'messages',
        question: 'How do I open Messages?',
        answer:
            'Open Messages from the sidebar when Two-Way Text is enabled under Settings → Features. Patients must have a valid cell number and SMS preference to participate.',
    },
    {
        id: 'messages-conversation',
        categoryId: 'messages',
        question: 'How do I reply to a patient text?',
        answer:
            'Select a conversation from the patient list, review the thread, type your reply, and send. Delivery and history for outbound clinic SMS also appear under Activity.',
    },
    {
        id: 'messages-find-patient',
        categoryId: 'messages',
        question: 'How do I find a patient conversation?',
        answer:
            'Use the patient list and search controls on the Messages page to locate a thread by name or number. Starting a new conversation requires a patient with SMS-capable contact details.',
    },

    // ── Surveys ────────────────────────────────────────────────
    {
        id: 'surveys-overview',
        categoryId: 'surveys',
        question: 'What are Surveys?',
        answer:
            'Surveys lets you create patient questionnaires, send them, and review results and graphs. Use templates to start quickly, then edit questions before publishing.',
    },
    {
        id: 'surveys-access',
        categoryId: 'surveys',
        question: 'How do I open Surveys?',
        answer:
            'Open Surveys from the sidebar when Surveys (and optionally Survey Reminders) are enabled for your clinic.',
    },
    {
        id: 'surveys-create',
        categoryId: 'surveys',
        question: 'How do I create a survey?',
        answer:
            'From Surveys, create a new survey or start from a template, then edit questions and settings on the survey edit page. Save before sending so patients receive the latest version.',
    },
    {
        id: 'surveys-results',
        categoryId: 'surveys',
        question: 'Where do I see survey results?',
        answer:
            'Open a survey\'s results and graphs pages from the Surveys list. Use results for individual responses and graphs for aggregate trends over time.',
    },

    // ── Articles ───────────────────────────────────────────────
    {
        id: 'articles-overview',
        categoryId: 'articles',
        question: 'What are Articles?',
        answer:
            'Articles is your patient education library. Browse the tree or list, open an article to review content, print it, and send education materials to patients when the Articles feature is enabled.',
    },
    {
        id: 'articles-access',
        categoryId: 'articles',
        question: 'How do I open Articles?',
        answer:
            'Open Articles from the sidebar when the Articles feature is enabled under Settings → Features.',
    },
    {
        id: 'articles-browse',
        categoryId: 'articles',
        question: 'How do I find an article?',
        answer:
            'Use the article tree or list view to browse categories, then open an item to read or print it. Search and folder navigation help when your library is large.',
    },
    {
        id: 'articles-send',
        categoryId: 'articles',
        question: 'How do I send an article to a patient?',
        answer:
            'Open the article, then use the send action from the article tools (or from a patient workflow that supports articles). Confirm the patient\'s email or SMS preference before sending.',
    },

    // ── Newsletter ─────────────────────────────────────────────
    {
        id: 'newsletter-overview',
        categoryId: 'newsletter',
        question: 'What is Newsletter?',
        answer:
            'Newsletter lets you create and send email or SMS newsletters to patients who have opted in. Use it for practice updates, seasonal notices, and educational campaigns.',
    },
    {
        id: 'newsletter-access',
        categoryId: 'newsletter',
        question: 'How do I open Newsletter?',
        answer:
            'Open Newsletter from the sidebar when Email Newsletter and/or SMS Newsletter features are enabled. Paths differ for create-email and create-sms flows.',
    },
    {
        id: 'newsletter-audience',
        categoryId: 'newsletter',
        question: 'Who receives newsletters?',
        answer:
            'Only patients with valid contact details and matching communication preferences for email or SMS are included. Invalid Details and patient Communication settings affect deliverability.',
    },
    {
        id: 'newsletter-vs-blast',
        categoryId: 'newsletter',
        question: 'How is Newsletter different from Blast?',
        answer:
            'Newsletter is for recurring practice communications and content updates. Blast is for one-off marketing pushes to selected lists. Both respect opt-in preferences; choose the tool that matches your campaign type.',
    },

    // ── Blast ──────────────────────────────────────────────────
    {
        id: 'blast-overview',
        categoryId: 'blast',
        question: 'What is Blast?',
        answer:
            'Blast sends one-time marketing email or SMS campaigns to selected patient lists. Use it for promotions, announcements, and targeted outreach outside the regular newsletter cadence.',
    },
    {
        id: 'blast-access',
        categoryId: 'blast',
        question: 'How do I open Blast?',
        answer:
            'Open Blast from the sidebar when Email Blast and/or SMS Blast features are enabled. Separate create-email and create-sms flows are available.',
    },
    {
        id: 'blast-send',
        categoryId: 'blast',
        question: 'How do I send a blast?',
        answer:
            'Choose email or SMS, build your message, select the audience, review the preview, then send. Track delivery afterward under Activity and Dashboard campaign stats when available.',
    },
    {
        id: 'blast-compliance',
        categoryId: 'blast',
        question: 'What should I check before sending a blast?',
        answer:
            'Confirm audience filters, opt-in preferences, and message content. Patients with invalid emails or phones will not receive the campaign—clean Invalid Details first for better delivery rates.',
    },

    // ── Clinic Settings ────────────────────────────────────────
    {
        id: 'settings-clinic-overview',
        categoryId: 'settings-clinic',
        question: 'What can I manage in Clinic Settings?',
        answer:
            'Clinic Settings covers practice info, hours, social links, contact lens brands, appointment notes, recall settings, and Web Booking (doctors, hours, appointment types, and visit reasons).',
    },
    {
        id: 'settings-clinic-access',
        categoryId: 'settings-clinic',
        question: 'How do I open Clinic Settings?',
        answer:
            'Go to Settings → Clinic from the sidebar. Use the in-page tabs to switch between General, Hours, Web Booking, Recall, and other clinic sections.',
    },
    {
        id: 'settings-clinic-info',
        categoryId: 'settings-clinic',
        question: 'How do I update clinic name, address, or logo?',
        answer:
            'Open the Clinic Info section, edit the fields, and save. These details appear in patient emails, headers, and public-facing booking pages.',
    },
    {
        id: 'settings-clinic-web-booking',
        categoryId: 'settings-clinic',
        question: 'Where do I configure online booking?',
        answer:
            'Under Clinic Settings → Web Booking, manage doctors, weekly and custom hours, appointment types, and visit reasons shown on your public booking form. Changes affect new Web Bookings requests.',
    },

    // ── Reminders ──────────────────────────────────────────────
    {
        id: 'settings-reminders-overview',
        categoryId: 'settings-reminders',
        question: 'What are Clinic Reminders?',
        answer:
            'Clinic Reminders control automated appointment reminder timing and channels. Review reminder categories, enable or adjust schedules, and keep outreach aligned with your clinic policies.',
    },
    {
        id: 'settings-reminders-access',
        categoryId: 'settings-reminders',
        question: 'How do I open Reminder Settings?',
        answer:
            'Go to Settings → Reminders. Reminder cards are grouped by category so you can review appointment, engagement, and related automated messages in one place.',
    },
    {
        id: 'settings-reminders-edit',
        categoryId: 'settings-reminders',
        question: 'How do I change a reminder?',
        answer:
            'Open the reminder card for the category you need, adjust timing or channel options, and save. Template wording for many reminders is edited under Settings → Templates.',
    },
    {
        id: 'settings-reminders-vs-templates',
        categoryId: 'settings-reminders',
        question: 'What is the difference between Reminders and Templates?',
        answer:
            'Reminders control when and whether automated messages send. Templates control the email and SMS wording. Configure both when launching a new outreach type.',
    },

    // ── Templates ──────────────────────────────────────────────
    {
        id: 'settings-templates-overview',
        categoryId: 'settings-templates',
        question: 'What are Templates?',
        answer:
            'Templates store the email and SMS content used for reminders, thank-you messages, surveys, reputation outreach, and other clinic communications.',
    },
    {
        id: 'settings-templates-access',
        categoryId: 'settings-templates',
        question: 'How do I open Templates?',
        answer:
            'Go to Settings → Templates. Templates are grouped by type (for example patient engagement, appointments, and reputation) so you can find the right message quickly.',
    },
    {
        id: 'settings-templates-edit',
        categoryId: 'settings-templates',
        question: 'How do I edit a template?',
        answer:
            'Select a template group and open the email or SMS version you need. Update the subject and body, keep required placeholders intact, then save. Preview when available before enabling related reminders.',
    },
    {
        id: 'settings-templates-placeholders',
        categoryId: 'settings-templates',
        question: 'What are template placeholders?',
        answer:
            'Placeholders such as patient name or clinic phone are replaced automatically when a message sends. Do not remove required placeholders or delivery and personalization may break.',
    },

    // ── Users ──────────────────────────────────────────────────
    {
        id: 'settings-users-overview',
        categoryId: 'settings-users',
        question: 'What are User Settings?',
        answer:
            'User Settings lets admins manage clinic staff accounts, roles, and access. Invite or update users so the right people can open feature pages and settings.',
    },
    {
        id: 'settings-users-access',
        categoryId: 'settings-users',
        question: 'How do I open User Settings?',
        answer:
            'Go to Settings → Users. You typically need an administrator role to add or change other users.',
    },
    {
        id: 'settings-users-roles',
        categoryId: 'settings-users',
        question: 'How do roles affect what someone sees?',
        answer:
            'Roles control which pages and settings a user can open. Feature toggles under Settings → Features still apply at the clinic level; roles further limit staff within those features.',
    },
    {
        id: 'settings-users-password',
        categoryId: 'settings-users',
        question: 'How does a user change their own password?',
        answer:
            'Each user opens Password Settings from the right account panel (click your profile in the top-right → Account Settings → Password Settings), or from Help → Get Support → Change Password. Admins manage accounts under Settings → Users but users update their own passwords.',
    },

    // ── Features ───────────────────────────────────────────────
    {
        id: 'settings-features-overview',
        categoryId: 'settings-features',
        question: 'What is Features?',
        answer:
            'Features shows which EyeconX modules are enabled for your clinic—Appointments, Messages, Surveys, Reputation, Optical Dispensary, newsletters, blasts, and more. Enabled features appear in the sidebar.',
    },
    {
        id: 'settings-features-access',
        categoryId: 'settings-features',
        question: 'How do I open Features?',
        answer:
            'Go to Settings → Features. Feature availability may be managed with EyeconX support depending on your subscription; contact support if you need a module turned on.',
    },
    {
        id: 'settings-features-missing-page',
        categoryId: 'settings-features',
        question: 'Why is a page missing from my sidebar?',
        answer:
            'Most specialty pages only appear when their feature is enabled and your user role allows access. Check Settings → Features, then confirm your role under Settings → Users. Help shortcuts also hide when a feature is off.',
    },

    // ── Invoices ───────────────────────────────────────────────
    {
        id: 'invoices-overview',
        categoryId: 'invoices',
        question: 'What are Invoices?',
        answer:
            'Invoices lists billing documents shared with your clinic. Open or download files, track newly added invoices, and keep financial paperwork in one place.',
    },
    {
        id: 'invoices-access',
        categoryId: 'invoices',
        question: 'How do I open Invoices?',
        answer:
            'Open your account panel from the top-right (click your profile), then under Account Settings click Invoices. New invoices may show a badge until they have been viewed.',
    },
    {
        id: 'invoices-download',
        categoryId: 'invoices',
        question: 'How do I download an invoice?',
        answer:
            'Find the invoice in the list and use the download or open action for that file. PDF and common document types are supported when provided by EyeconX billing.',
    },
    {
        id: 'invoices-new',
        categoryId: 'invoices',
        question: 'What does a “new” invoice mean?',
        answer:
            'Invoices you have not opened yet can appear as new. Opening or marking them seen clears the new indicator for your browser on that device.',
    },

    // ── Eye Draw ───────────────────────────────────────────────
    {
        id: 'eye-draw-overview',
        categoryId: 'eye-draw',
        question: 'What is Eye Draw?',
        answer:
            'Eye Draw provides clinical illustration templates for eye conditions. Browse sections such as Eye Anatomy, Eye Lids, Cornea/Sclera, Cataract, Glaucoma, Retina, Vitreous, and Pathology, then open a template card to illustrate findings.',
    },
    {
        id: 'eye-draw-access',
        categoryId: 'eye-draw',
        question: 'How do I open Eye Draw?',
        answer:
            'Open Eye Draw from the sidebar or clinic tools menu. The page header shows how many templates are available.',
    },
    {
        id: 'eye-draw-templates',
        categoryId: 'eye-draw',
        question: 'How do I use a drawing template?',
        answer:
            'Pick a clinical section, choose a template card, and use the drawing tools on that template to mark findings. Save or export according to the tools shown on the template screen.',
    },

    // ── What's New ─────────────────────────────────────────────
    {
        id: 'whats-new-overview',
        categoryId: 'whats-new',
        question: 'What is What’s New?',
        answer:
            'What’s New lists recent EyeconX Clinic release notes—product improvements, new filters, and workflow changes—so your team can learn updates without leaving the app.',
    },
    {
        id: 'whats-new-access',
        categoryId: 'whats-new',
        question: 'How do I open What’s New?',
        answer:
            'Open What’s New from the left sidebar (near Help & Support), or from the Support section in your right account panel (click your profile in the top-right). Entries are ordered with the newest releases first.',
    },
    {
        id: 'whats-new-help',
        categoryId: 'whats-new',
        question: 'What if I need more detail than a release note?',
        answer:
            'Open Help & Support and search FAQs for the page mentioned in the release—for example, search Recall Pool under Recalls. For account-specific questions, use Email Support under Get Support.',
    },
];

export const PAGE_SUPPORT_OPTIONS = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        description: 'Review patient, email, SMS, campaign, and appointment overview charts.',
        icon: 'bi-speedometer2',
        theme: 'primary',
        color: 'primary',
        actionLabel: 'Go to Dashboard',
        to: '/dashboard',
    },
    {
        id: 'web-bookings',
        title: 'Web Bookings',
        description: 'Review and manage appointment requests from your clinic website.',
        icon: 'bi-calendar-check',
        theme: 'cyan',
        color: 'info',
        actionLabel: 'Go to Web Bookings',
        to: '/web-bookings',
        featureKey: 'booking',
    },
    {
        id: 'cancellation-followups',
        title: 'Cancellation Followups',
        description: 'Reach patients with cancelled appointments and help them rebook.',
        icon: 'bi-calendar-x',
        theme: 'danger',
        color: 'danger',
        actionLabel: 'Go to Cancellation Followups',
        to: '/cancellation-followups',
    },
    {
        id: 'patient-engagement',
        title: 'Patient Engagement',
        description: 'Send thank-you follow-ups and manage post-visit outreach.',
        icon: 'bi-person-hearts',
        theme: 'pink',
        color: 'primary',
        actionLabel: 'Go to Patient Engagement',
        to: '/patient-engagement',
    },
    {
        id: 'reputation',
        title: 'Reputation',
        description: 'Monitor review links, patient comments, and feedback.',
        icon: 'bi-star',
        theme: 'warning',
        color: 'warning',
        actionLabel: 'Go to Reputation',
        to: '/reputation',
        featureKey: 'reputation',
    },
    {
        id: 'recalls',
        title: 'Recalls',
        description: 'Upload recall files, browse the patient pool, and update recall stages.',
        icon: 'bi-person-check',
        theme: 'orange',
        color: 'warning',
        actionLabel: 'Go to Recalls',
        to: '/recalls/requests',
        featureKey: 'recall',
    },
    {
        id: 'messages',
        title: 'Messages',
        description: 'Two-way text conversations with patients.',
        icon: 'bi-chat-left-text',
        theme: 'success',
        color: 'success',
        actionLabel: 'Go to Messages',
        to: '/messages',
        featureKey: 'twoWayText',
    },
    {
        id: 'surveys',
        title: 'Surveys',
        description: 'Create surveys and review patient responses.',
        icon: 'bi-clipboard-check',
        theme: 'teal',
        color: 'success',
        actionLabel: 'Go to Surveys',
        to: '/surveys',
        featureKey: 'survey',
    },
    {
        id: 'newsletter',
        title: 'Newsletter',
        description: 'Send email or SMS newsletters to opted-in patients.',
        icon: 'bi-envelope-paper',
        theme: 'pink',
        color: 'primary',
        actionLabel: 'Go to Newsletter',
        to: '/newsletter/create-email',
        featureKey: 'newsletterEmail',
    },
    {
        id: 'blast',
        title: 'Blast',
        description: 'Send one-time email or SMS marketing campaigns.',
        icon: 'bi-megaphone',
        theme: 'danger',
        color: 'danger',
        actionLabel: 'Go to Blast',
        to: '/blast/create-email',
        featureKey: 'blastEmail',
    },
    {
        id: 'invoices',
        title: 'Invoices',
        description: 'Open from your right account panel to view and download clinic invoices.',
        icon: 'bi-receipt',
        theme: 'slate',
        color: 'secondary',
        actionLabel: 'Go to Invoices',
        to: '/invoices',
    },
    {
        id: 'settings-clinic',
        title: 'Clinic Settings',
        description: 'Update clinic info, hours, web booking, and recall settings.',
        icon: 'bi-building',
        theme: 'slate',
        color: 'secondary',
        actionLabel: 'Go to Clinic Settings',
        to: '/settings/clinic',
    },
    {
        id: 'settings-reminders',
        title: 'Reminders',
        description: 'Configure automated reminder schedules and channels.',
        icon: 'bi-bell',
        theme: 'warning',
        color: 'warning',
        actionLabel: 'Go to Reminders',
        to: '/settings/reminders',
    },
    {
        id: 'settings-templates',
        title: 'Templates',
        description: 'Edit email and SMS templates for clinic outreach.',
        icon: 'bi-file-earmark-text',
        theme: 'cyan',
        color: 'info',
        actionLabel: 'Go to Templates',
        to: '/settings/templates',
    },
    {
        id: 'eye-draw',
        title: 'Eye Draw',
        description: 'Illustrate eye conditions on clinical drawing templates.',
        icon: 'bi-easel',
        theme: 'indigo',
        color: 'primary',
        actionLabel: 'Go to Eye Draw',
        to: '/eye-draw',
    },
    {
        id: 'whats-new',
        title: "What's New",
        description: 'Also in your right account panel Support section—read recent release notes.',
        icon: 'bi-stars',
        theme: 'primary',
        color: 'primary',
        actionLabel: "Go to What's New",
        to: '/whats-new',
    },
];
